import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Pagination from '../../shared/components/Pagination';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { extractErrorMessage } from '../../shared/utils/errors';
import { PaginatedResponse, UserBalanceGroup } from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import { BalanceFilters, listBalances } from './api';

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX', 'USD'];

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '0';
  const decimal = amount / SMALLEST_UNIT_DIVISOR;
  return decimal.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
};

const BalancesPage: React.FC = () => {
  const [balances, setBalances] = useState<PaginatedResponse<UserBalanceGroup> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<BalanceFilters>({});
  const [draftFilters, setDraftFilters] = useState<BalanceFilters>({});

  const [profileUserId, setProfileUserId] = useState<number | null>(null);

  const load = useCallback(async (page: number, filtersToUse: BalanceFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listBalances({ ...filtersToUse, page, per_page: 20 });
      setBalances(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load balances'));
      setBalances(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (balances?.data) {
    for (const group of balances.data) userIds.push(group.user_id);
  }
  const userNames = useUserNames(userIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Account Balances</h1>
          <p className="text-slate-400 mt-1">
            All users holding funds — per-coin balances and estimated USD total.
          </p>
        </div>

        <div className="card p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !balances?.data.length ? (
            <div className="p-12 text-center text-slate-400">No balances found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">User</th>
                      <th className="table-header">Coin</th>
                      <th className="table-header text-right">Balance</th>
                      <th className="table-header text-right">Available</th>
                      <th className="table-header text-right">In escrow</th>
                      <th className="table-header text-right">USD value</th>
                      <th className="table-header text-right">Total (USD)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {balances.data.map((group) =>
                      group.accounts.map((account, accountIndex) => (
                        <tr
                          key={`${group.user_id}-${account.currency}`}
                          className="hover:bg-slate-800/30 transition-colors"
                        >
                          {accountIndex === 0 && (
                            <td
                              className="table-cell align-top border-r border-slate-800/60"
                              rowSpan={group.accounts.length}
                            >
                              <button
                                type="button"
                                onClick={() => setProfileUserId(group.user_id)}
                                className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                                title={`Open profile of user #${group.user_id}`}
                              >
                                {formatUserLabel(group.user_id, userNames)}
                              </button>
                              <p className="text-xs text-slate-500 font-mono mt-0.5">
                                #{group.user_id}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                {group.accounts.length} coin{group.accounts.length === 1 ? '' : 's'}
                              </p>
                            </td>
                          )}
                          <td className="table-cell text-white font-medium whitespace-nowrap">
                            {account.currency}
                          </td>
                          <td className="table-cell text-right font-mono text-slate-200">
                            {formatCryptoAmount(account.balance)}
                          </td>
                          <td className="table-cell text-right font-mono text-emerald-400">
                            {formatCryptoAmount(account.available)}
                          </td>
                          <td className="table-cell text-right font-mono text-amber-400">
                            {formatCryptoAmount(account.held)}
                          </td>
                          <td
                            className="table-cell text-right font-mono text-slate-300"
                            title={
                              account.usd_rate != null
                                ? `Rate: ${formatUsd(account.usd_rate)} / ${account.currency}`
                                : 'No exchange rate available'
                            }
                          >
                            {formatUsd(account.usd_value)}
                          </td>
                          {accountIndex === 0 && (
                            <td
                              className="table-cell text-right align-top font-mono text-white border-l border-slate-800/60"
                              rowSpan={group.accounts.length}
                            >
                              {formatUsd(group.total_usd)}
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {balances.last_page > 1 && (
                <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {balances.total} user{balances.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={balances.current_page}
                    lastPage={balances.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <UserDetailModal
        isOpen={profileUserId !== null}
        onClose={() => setProfileUserId(null)}
        userId={profileUserId}
      />
    </Layout>
  );
};

export default BalancesPage;
